import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Play, Database, Plus, Bell, Users, LineChart } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const KQL_SAMPLES = [
  "Perf | where CounterName == '% Processor Time' | where CounterValue > 80 | project Computer, CounterValue",
  "AzureActivity | where ActivityStatus == 'Failed' | project OperationName, Caller, ResourceGroup",
  "Heartbeat | summarize Count = count() by Computer",
  "AzureActivity | summarize Count = count() by OperationName | order by Count desc",
  "AppRequests | where Success == false | summarize Failures = count() by ResultCode",
];

const OPERATIONS = [
  "Microsoft.Compute/virtualMachines/delete",
  "Microsoft.Compute/virtualMachines/write",
  "Microsoft.Network/networkSecurityGroups/securityRules/write",
  "Microsoft.Storage/storageAccounts/delete",
  "Microsoft.Authorization/roleAssignments/write",
];

export default function LogAnalytics({ initialTab }) {
  const { state, refresh } = useAzure();
  const rgs = state?.resource_groups || [];
  const [cfg, setCfg] = useState({ workspaces: [], action_groups: [], activity_alerts: [] });
  const [tab, setTab] = useState(initialTab || "kql");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/azure/monitor/config");
      setCfg(data);
    } catch (e) { /* first load */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const after = async () => { await load(); refresh?.(); };

  return (
    <div data-testid="az-log-analytics">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-4">
        <LineChart className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-semibold">Azure Monitor — Log Analytics, KQL &amp; Alerts</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="workspaces" data-testid="az-mon-tab-workspaces"><Database className="h-4 w-4 mr-1.5" />Workspaces</TabsTrigger>
          <TabsTrigger value="kql" data-testid="az-mon-tab-kql"><Play className="h-4 w-4 mr-1.5" />KQL query</TabsTrigger>
          <TabsTrigger value="action-groups" data-testid="az-mon-tab-ag"><Users className="h-4 w-4 mr-1.5" />Action groups</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="az-mon-tab-alerts"><Bell className="h-4 w-4 mr-1.5" />Activity log alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="workspaces" className="mt-4"><Workspaces rgs={rgs} cfg={cfg} after={after} /></TabsContent>
        <TabsContent value="kql" className="mt-4"><KqlRunner cfg={cfg} /></TabsContent>
        <TabsContent value="action-groups" className="mt-4"><ActionGroups rgs={rgs} cfg={cfg} after={after} /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><ActivityAlerts rgs={rgs} cfg={cfg} after={after} /></TabsContent>
      </Tabs>
    </div>
  );
}

function RgSelect({ value, onChange, rgs, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
      <option value="">Select a resource group…</option>
      {rgs.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
    </select>
  );
}

function Workspaces({ rgs, cfg, after }) {
  const [name, setName] = useState("");
  const [rg, setRg] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !rg) return toast.error("Enter a name and pick a resource group.");
    setBusy(true);
    try {
      await api.post("/azure/monitor/workspaces", { name, resource_group: rg, location: "eastus" });
      toast.success(`Workspace '${name}' created`); setName(""); await after();
    } catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-border p-4 bg-card">
        <div className="text-sm font-medium mb-3">Create a Log Analytics workspace</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Name</label>
            <Input data-testid="az-law-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="law-hmg-ops" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Resource group</label>
            <div className="mt-1"><RgSelect testid="az-law-rg" value={rg} onChange={setRg} rgs={rgs} /></div></div>
        </div>
        <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={create} disabled={busy} data-testid="az-law-create">
          <Plus className="h-4 w-4" /> {busy ? "Creating…" : "Create workspace"}
        </Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-law-list">
        {cfg.workspaces.length === 0 && <div className="text-sm text-muted-foreground">No workspaces yet.</div>}
        {cfg.workspaces.map((w) => (
          <div key={w.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`az-law-${w.name}`}>
            <Database className="h-4 w-4 text-[#0078d4]" />
            <div className="min-w-0"><div className="text-sm font-medium">{w.name}</div>
              <div className="text-xs text-muted-foreground">{w.resource_group} · retention {w.retention_days}d · {w.sku}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KqlRunner({ cfg }) {
  const [ws, setWs] = useState("");
  const [query, setQuery] = useState(KQL_SAMPLES[0]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!ws && cfg.workspaces[0]) setWs(cfg.workspaces[0].id); }, [cfg, ws]);

  const run = async () => {
    if (!ws) return toast.error("Create a workspace first (Workspaces tab).");
    setBusy(true);
    try {
      const { data } = await api.post("/azure/monitor/kql", { workspace_id: ws, query });
      setRes(data);
    } catch (e) { toast.error(azErr(e)); setRes(null); } finally { setBusy(false); }
  };

  if (cfg.workspaces.length === 0)
    return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="az-kql-empty">Create a Log Analytics workspace first to run KQL queries.</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Workspace</span>
        <select data-testid="az-kql-ws" value={ws} onChange={(e) => setWs(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-sm">
          {cfg.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
      <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Database className="h-3.5 w-3.5" /> Tables: Perf, Heartbeat, AzureActivity, AppRequests. Supports where, project, summarize, count, distinct, order by, top, take.</div>
      <Textarea data-testid="az-kql-query" value={query} onChange={(e) => setQuery(e.target.value)} rows={3} className="font-mono text-sm" />
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <Button size="sm" onClick={run} disabled={busy} data-testid="az-kql-run" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Play className="h-4 w-4" /> {busy ? "Running…" : "Run"}</Button>
        {KQL_SAMPLES.map((s, i) => <button key={i} className="text-xs text-[#0078d4] hover:underline" onClick={() => setQuery(s)}>Sample {i + 1}</button>)}
      </div>
      {res && (
        <div className="mt-4 border border-border rounded-md overflow-hidden" data-testid="az-kql-results">
          <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border">{res.count} row(s) from {res.table}</div>
          <div className="overflow-x-auto">
            <Table><TableHeader><TableRow>{res.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {res.rows.map((r, i) => (<TableRow key={i}>{res.columns.map((c) => <TableCell key={c} className="text-xs">{String(r[c])}</TableCell>)}</TableRow>))}
                {res.rows.length === 0 && <TableRow><TableCell colSpan={Math.max(1, res.columns.length)} className="text-center text-muted-foreground py-6">No matching records.</TableCell></TableRow>}
              </TableBody></Table>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionGroups({ rgs, cfg, after }) {
  const [name, setName] = useState("");
  const [rg, setRg] = useState("");
  const [shortName, setShortName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !rg || !email.trim()) return toast.error("Name, resource group and an email receiver are required.");
    setBusy(true);
    try {
      await api.post("/azure/monitor/action-groups", { name, resource_group: rg, short_name: shortName || name.slice(0, 12),
        receivers: [{ name: "Ops Email", type: "email", value: email }] });
      toast.success(`Action group '${name}' created`); setName(""); setEmail(""); await after();
    } catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-border p-4 bg-card">
        <div className="text-sm font-medium mb-3">Create an action group</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Name</label>
            <Input data-testid="az-ag-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ag-ops-oncall" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Resource group</label>
            <div className="mt-1"><RgSelect testid="az-ag-rg" value={rg} onChange={setRg} rgs={rgs} /></div></div>
          <div><label className="text-xs text-muted-foreground">Short name (≤12)</label>
            <Input data-testid="az-ag-short" value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="opsoncall" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Email receiver</label>
            <Input data-testid="az-ag-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="oncall@hmg.com" className="mt-1" /></div>
        </div>
        <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={create} disabled={busy} data-testid="az-ag-create">
          <Plus className="h-4 w-4" /> {busy ? "Creating…" : "Create action group"}
        </Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-ag-list">
        {cfg.action_groups.length === 0 && <div className="text-sm text-muted-foreground">No action groups yet.</div>}
        {cfg.action_groups.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`az-ag-${a.name}`}>
            <Users className="h-4 w-4 text-[#0078d4]" />
            <div className="min-w-0"><div className="text-sm font-medium">{a.name}</div>
              <div className="text-xs text-muted-foreground">{a.resource_group} · {(a.receivers || []).map((r) => r.value).join(", ")}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityAlerts({ rgs, cfg, after }) {
  const [name, setName] = useState("");
  const [rg, setRg] = useState("");
  const [op, setOp] = useState(OPERATIONS[0]);
  const [ag, setAg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!ag && cfg.action_groups[0]) setAg(cfg.action_groups[0].id); }, [cfg, ag]);
  const create = async () => {
    if (!name.trim() || !rg || !ag) return toast.error("Name, resource group and an action group are required.");
    setBusy(true);
    try {
      await api.post("/azure/monitor/activity-alerts", { name, resource_group: rg, operation_name: op, action_group_id: ag });
      toast.success(`Alert '${name}' created`); setName(""); await after();
    } catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };
  if (cfg.action_groups.length === 0)
    return <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="az-alert-empty">Create an action group first (Action groups tab) so an alert has somewhere to notify.</div>;
  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-border p-4 bg-card">
        <div className="text-sm font-medium mb-3">Create an activity log alert</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Name</label>
            <Input data-testid="az-alert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="alert-vm-delete" className="mt-1" /></div>
          <div><label className="text-xs text-muted-foreground">Resource group</label>
            <div className="mt-1"><RgSelect testid="az-alert-rg" value={rg} onChange={setRg} rgs={rgs} /></div></div>
          <div><label className="text-xs text-muted-foreground">Operation (signal)</label>
            <select data-testid="az-alert-op" value={op} onChange={(e) => setOp(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-2 py-2 text-sm">
              {OPERATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
          <div><label className="text-xs text-muted-foreground">Action group</label>
            <select data-testid="az-alert-ag" value={ag} onChange={(e) => setAg(e.target.value)} className="w-full mt-1 rounded-md border border-input bg-background px-2 py-2 text-sm">
              {cfg.action_groups.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></div>
        </div>
        <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={create} disabled={busy} data-testid="az-alert-create">
          <Plus className="h-4 w-4" /> {busy ? "Creating…" : "Create alert"}
        </Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="az-alert-list">
        {cfg.activity_alerts.length === 0 && <div className="text-sm text-muted-foreground">No activity log alerts yet.</div>}
        {cfg.activity_alerts.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`az-alert-${a.name}`}>
            <Bell className="h-4 w-4 text-amber-500" />
            <div className="min-w-0"><div className="text-sm font-medium">{a.name}</div>
              <div className="text-xs text-muted-foreground truncate">{a.operation_name} → {a.action_group_name}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}
